import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_castel-new-look/artifacts/8py7eydi_logo-removebg-preview-300x300.png";

const Logo = ({ inverse }) => (
  <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
    <img
      src={LOGO_URL}
      alt="Castel Construct"
      className={`h-10 w-10 md:h-12 md:w-12 object-contain transition-transform duration-500 group-hover:rotate-[3deg] ${
        inverse ? "invert brightness-0" : ""
      }`}
    />
    <span className={`hidden sm:block font-display text-xl leading-none ${inverse ? "text-white" : "text-black"}`}>
      Castel<span className="italic"> Construct</span>
    </span>
  </Link>
);

const LangSwitcher = ({ inverse }) => {
  const { lang, setLang } = useLang();
  const langs = ["ro", "en", "ru"];
  return (
    <div className="flex items-center gap-px font-mono text-xs" data-testid="language-switcher">
      {langs.map((l, i) => (
        <React.Fragment key={l}>
          <button
            data-testid={`lang-${l}`}
            onClick={() => setLang(l)}
            className={`uppercase px-2 py-1 transition-colors ${
              lang === l
                ? inverse ? "text-white" : "text-black"
                : inverse ? "text-white/40 hover:text-white" : "text-neutral-400 hover:text-black"
            }`}
          >
            {l}
          </button>
          {i < langs.length - 1 && <span className={inverse ? "text-white/30" : "text-neutral-300"}>·</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const sectionHref = (section) => (onHome ? `#${section}` : `/#${section}`);

  const navItems = [
    { id: "about", section: "about", label: t.nav.about },
    { id: "services", section: "services", label: t.nav.services },
    { id: "process", section: "process", label: t.nav.process },
    { id: "contact", section: "contact", label: t.nav.contact },
  ];

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-[var(--bg)]/90 backdrop-blur-lg border-b border-[var(--border)]" : "bg-transparent"
        }`}
      >
        <div className="container-page flex items-center justify-between h-16 md:h-20">
          <Logo />

          <nav className="hidden md:flex items-center gap-8" data-testid="navbar-nav">
            {navItems.map((n) => (
              <a
                key={n.id}
                href={sectionHref(n.section)}
                data-testid={`nav-${n.id}`}
                className="text-sm text-neutral-700 hover:text-black transition-colors relative group"
              >
                {n.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-black transition-all group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4 md:gap-6">
            <LangSwitcher />
            <a
              href={sectionHref("contact")}
              data-testid="navbar-cta"
              className="hidden md:inline-flex items-center gap-2 bg-black text-white text-sm px-5 py-2.5 hover:bg-[var(--accent)] transition-colors"
            >
              {t.nav.cta}
              <span aria-hidden>→</span>
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 -mr-2"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-[60] bg-[var(--bg)] transition-transform duration-500 ${
          open ? "translate-x-0" : "translate-x-full"
        } md:hidden`}
      >
        <div className="container-page flex items-center justify-between h-16">
          <Logo />
          <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="p-2 -mr-2" aria-label="Close menu">
            <X size={24} />
          </button>
        </div>
        <nav className="container-page pt-12 flex flex-col gap-6">
          {navItems.map((n) => (
            <a
              key={n.id}
              href={sectionHref(n.section)}
              data-testid={`mobile-nav-${n.id}`}
              onClick={() => setOpen(false)}
              className="font-display text-4xl"
            >
              {n.label}
            </a>
          ))}
          <a
            href={sectionHref("contact")}
            data-testid="mobile-cta"
            onClick={() => setOpen(false)}
            className="mt-6 inline-flex items-center justify-between bg-black text-white px-5 py-4"
          >
            <span>{t.nav.cta}</span>
            <span aria-hidden>→</span>
          </a>
        </nav>
      </div>
    </>
  );
};

export default Navbar;
